import React, { useState } from 'react';
import { AlertTriangleIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon } from 'lucide-react';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
type PrecisionSetting = {
  id: string;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  precision: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export function DecimalPrecisionConfiguration({
  locale = 'en-US'
}) {
  const [precisionSettings, setPrecisionSettings] = useState<PrecisionSetting[]>([{
    id: 'prec-001',
    scope: 'Global',
    precision: 2,
    isActive: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'prec-002',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    precision: 4,
    isActive: true,
    createdAt: '2023-03-15T00:00:00Z',
    updatedAt: '2023-03-15T00:00:00Z'
  }, {
    id: 'prec-003',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-tokyo',
    orgUnitName: 'Tokyo Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Tokyo Office',
    precision: 2,
    isActive: true,
    createdAt: '2023-04-01T00:00:00Z',
    updatedAt: '2023-04-01T00:00:00Z'
  }]);
  const [showForm, setShowForm] = useState(false);
  const [editingSetting, setEditingSetting] = useState<PrecisionSetting | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Form state
  const [formScope, setFormScope] = useState<'Global' | 'OrgUnit'>('OrgUnit');
  const [formPrecision, setFormPrecision] = useState(2);
  const [formOrgUnitId, setFormOrgUnitId] = useState('');
  const [formOrgUnit, setFormOrgUnit] = useState<OrgUnit | null>(null);
  const handleAddNew = () => {
    setEditingSetting(null);
    setFormScope('OrgUnit');
    setFormPrecision(2);
    setFormOrgUnitId('');
    setFormOrgUnit(null);
    setShowForm(true);
  };
  const handleEdit = (setting: PrecisionSetting) => {
    setEditingSetting(setting);
    setFormScope(setting.scope);
    setFormPrecision(setting.precision);
    setFormOrgUnitId(setting.orgUnitId || '');
    setShowForm(true);
  };
  const handleDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setPrecisionSettings(precisionSettings.filter(s => s.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleSave = () => {
    if (editingSetting) {
      // Update existing
      setPrecisionSettings(precisionSettings.map(s => s.id === editingSetting.id ? {
        ...s,
        scope: formScope,
        orgUnitId: formScope === 'OrgUnit' ? formOrgUnitId : undefined,
        orgUnitName: formScope === 'OrgUnit' ? formOrgUnit?.name : undefined,
        orgUnitPath: formScope === 'OrgUnit' ? formOrgUnit?.path : undefined,
        precision: formPrecision,
        updatedAt: new Date().toISOString()
      } : s));
    } else {
      // Create new
      const newSetting: PrecisionSetting = {
        id: `prec-${Date.now()}`,
        scope: formScope,
        orgUnitId: formScope === 'OrgUnit' ? formOrgUnitId : undefined,
        orgUnitName: formScope === 'OrgUnit' ? formOrgUnit?.name : undefined,
        orgUnitPath: formScope === 'OrgUnit' ? formOrgUnit?.path : undefined,
        precision: formPrecision,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setPrecisionSettings([...precisionSettings, newSetting]);
    }
    setShowForm(false);
    setEditingSetting(null);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingSetting(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setFormOrgUnitId(orgUnitId);
    setFormOrgUnit(orgUnit);
  };
  const globalSetting = precisionSettings.find(s => s.scope === 'Global');
  const orgUnitSettings = precisionSettings.filter(s => s.scope === 'OrgUnit');
  return <div data-id="element-15879">
      <div className="mb-8" data-id="element-15880">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-15881">
          Decimal Precision Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15882">
          Configure decimal places for currency fields at global and
          organisation unit levels
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6" data-id="element-15883">
        <div className="flex" data-id="element-15884">
          <div className="flex-shrink-0" data-id="element-15885">
            <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-15886" />
          </div>
          <div className="ml-3" data-id="element-15887">
            <p className="text-sm text-yellow-700" data-id="element-15888">
              ⚠️ Changing precision settings will affect currency fields across
              RapidP2P. OrgUnit-specific settings override the global default.
              Changes should only be made by authorised financial
              administrators.
            </p>
          </div>
        </div>
      </div>

      {!showForm ? <>
          {/* Global Default Setting */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-15889">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-15890">
              <div className="flex items-center justify-between" data-id="element-15891">
                <div data-id="element-15892">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15893">
                    Global Default
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-15894">
                    Default precision applied to all organisation units unless
                    overridden
                  </p>
                </div>
                {globalSetting && <button onClick={() => handleEdit(globalSetting)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15895">
                    <EditIcon className="h-4 w-4 mr-2" data-id="element-15896" />
                    Edit
                  </button>}
              </div>
            </div>
            {globalSetting && <div className="px-4 py-5 sm:px-6" data-id="element-15897">
                <div className="flex items-center justify-between" data-id="element-15898">
                  <div data-id="element-15899">
                    <span className="text-sm font-medium text-gray-500" data-id="element-15900">
                      Decimal Places:
                    </span>
                    <span className="ml-2 text-2xl font-bold text-gray-900" data-id="element-15901">
                      {globalSetting.precision}
                    </span>
                    <span className="ml-2 text-sm text-gray-500" data-id="element-15902">
                      (
                      {globalSetting.precision === 2 ? 'Standard' : 'High Precision'}
                      )
                    </span>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-15903" />
                </div>
              </div>}
          </div>

          {/* OrgUnit Overrides */}
          <div className="bg-white shadow rounded-lg" data-id="element-15904">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-15905">
              <div className="flex items-center justify-between" data-id="element-15906">
                <div data-id="element-15907">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15908">
                    Organisation Unit Overrides
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-15909">
                    Specific precision settings for individual organisation
                    units
                  </p>
                </div>
                <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15910">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-15911" />
                  Add Override
                </button>
              </div>
            </div>

            {orgUnitSettings.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-15912">
                <p className="text-sm text-gray-500" data-id="element-15913">
                  No organisation unit overrides configured. All organisation
                  units use the global default.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-15914">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-15915">
                  <thead className="bg-gray-50" data-id="element-15916">
                    <tr data-id="element-15917">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15918">
                        Organisation Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15919">
                        Decimal Places
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15920">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15921">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-15922">
                    {orgUnitSettings.map(setting => <tr key={setting.id} data-id="element-15923">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15924">
                          <div className="text-sm font-medium text-gray-900" data-id="element-15925">
                            {setting.orgUnitName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-15926">
                            {setting.orgUnitPath}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15927">
                          <div className="text-sm font-bold text-gray-900" data-id="element-15928">
                            {setting.precision} decimal places
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-15929">
                            {setting.precision === 2 ? 'Standard' : 'High Precision'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-15930">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-15931">
                            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-15932" />
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-15933">
                          <button onClick={() => handleEdit(setting)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-15934">
                            <EditIcon className="h-4 w-4 inline" data-id="element-15935" />
                          </button>
                          <button onClick={() => handleDelete(setting.id)} className="text-red-600 hover:text-red-900" data-id="element-15936">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-15937" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </> : <div className="bg-white shadow rounded-lg" data-id="element-15938">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-15939">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15940">
              {editingSetting ? 'Edit Precision Setting' : 'Add Precision Override'}
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6" data-id="element-15941">
            {/* Scope */}
            <div data-id="element-15942">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-15943">
                Scope
              </label>
              <select value={formScope} onChange={e => setFormScope(e.target.value as 'Global' | 'OrgUnit')} disabled={editingSetting?.scope === 'Global'} className="block w-full max-w-xs shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed" data-id="element-15944">
                <option value="Global" data-id="element-15945">Global (Default)</option>
                <option value="OrgUnit" data-id="element-15946">Organisation Unit</option>
              </select>
              {editingSetting?.scope === 'Global' && <p className="mt-1 text-xs text-gray-500" data-id="element-15947">
                  Global scope cannot be changed
                </p>}
            </div>

            {/* OrgUnit Selector */}
            {formScope === 'OrgUnit' && <div data-id="element-15948">
                <OrgUnitSelector value={formOrgUnitId} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-15949" />
              </div>}

            {/* Precision */}
            <div data-id="element-15950">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-15951">
                Decimal Precision
              </label>
              <select value={formPrecision} onChange={e => setFormPrecision(parseInt(e.target.value))} className="block w-full max-w-xs shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md" data-id="element-15952">
                <option value={2} data-id="element-15953">2 Decimal Places (Standard)</option>
                <option value={4} data-id="element-15954">4 Decimal Places (High Precision)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500" data-id="element-15955">
                Defines how many decimal places are stored and displayed for
                currency values
              </p>
            </div>
          </div>

          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-3" data-id="element-15956">
            <button onClick={handleCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15957">
              Cancel
            </button>
            <button onClick={handleSave} disabled={formScope === 'OrgUnit' && !formOrgUnitId} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-15958">
              {editingSetting ? 'Update' : 'Create'}
            </button>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-15959">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-15960">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-15961">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-15962"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-15963">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-15964">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-15965">
                <div className="sm:flex sm:items-start" data-id="element-15966">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-15967">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-15968" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-15969">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15970">
                      Delete Precision Override
                    </h3>
                    <div className="mt-2" data-id="element-15971">
                      <p className="text-sm text-gray-500" data-id="element-15972">
                        Are you sure you want to delete this precision override?
                        The OrgUnit will revert to using the tenant default
                        precision.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-15973">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-15974">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-15975">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}